import { GetTokenSilentlyOptions, useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import jwt_decode from "jwt-decode";
import LogRocket from "logrocket";
import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { Outlet } from "react-router-dom";

import UserContext from "context/UserContext";

import Loading from "components/pages/Loading";

import { error, userHasAdminRole, userIsOrganizationOwner } from "utils";
import { UserContextType } from "utils/types";

const audience = process.env.REACT_APP_AUTH0_AUDIENCE as string;
const tokenNamespace = process.env.REACT_APP_TOKEN_NAMESPACE as string;

const AuthenticatedLayout = () => {
  const {
    isAuthenticated,
    isLoading,
    getAccessTokenSilently,
    loginWithRedirect,
  } = useAuth0();
  const [userRefreshToggle, setUserRefreshToggle] = useState(false);
  const [userContext, setUserContext] = useState<UserContextType>(
    {} as UserContextType
  );

  useEffect(() => {
    if (isLoading || isAuthenticated) {
      return;
    }

    loginWithRedirect({
      appState: { returnTo: window.location.pathname + window.location.search },
    });
  }, [isLoading, isAuthenticated, loginWithRedirect]);

  useEffect(() => {
    async function getToken() {
      try {
        const cacheMode: GetTokenSilentlyOptions["cacheMode"] = "off";
        const token = await getAccessTokenSilently({
          cacheMode,
          authorizationParams: { audience },
        });
        axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

        const decoded = jwt_decode(token) as any;
        const email = decoded[tokenNamespace].email || "";
        const normalizedEmail = email.toLowerCase();
        LogRocket.identify(decoded[tokenNamespace].email, {
          email: decoded[tokenNamespace].email,
          customerId: decoded[tokenNamespace].customerid,
        });
        const customerAccessData = await userIsOrganizationOwner();
        const hasAdminRole = userHasAdminRole(
          decoded[tokenNamespace].roles || []
        );
        const canOverrideCustomer =
          normalizedEmail.endsWith("@passiolife.com") ||
          normalizedEmail.startsWith("ae.branton9") ||
          hasAdminRole;
        const overrideCustomerId = canOverrideCustomer
          ? localStorage.getItem("passio.customerOverrideId") || ""
          : "";
        if (!canOverrideCustomer) {
          localStorage.removeItem("passio.customerOverrideId");
        }

        const uc: UserContextType = {
          email,
          roles: decoded[tokenNamespace].roles,
          customerid:
            overrideCustomerId ||
            decoded[tokenNamespace].customerid ||
            customerAccessData.customerId,
          isOrgOwner: customerAccessData.isOwner,
          canOverrideCustomer,
          customerOverrideId: overrideCustomerId || undefined,
          setUserRefreshToggle: setUserRefreshToggle,
          userRefreshToggle: userRefreshToggle,
        };
        setUserContext(uc);

        if (overrideCustomerId) {
          axios.defaults.params = {
            ...(axios.defaults.params || {}),
            customerId: overrideCustomerId,
          };
        } else if (
          axios.defaults.params &&
          "customerId" in axios.defaults.params
        ) {
          const nextParams = {
            ...(axios.defaults.params as Record<string, unknown>),
          };
          delete nextParams.customerId;
          axios.defaults.params = nextParams;
        }

        ReactGA.event("login");
      } catch (err: any) {
        error(err.message || "Could not retrieve access token.");
      }
    }

    if (isAuthenticated) {
      getToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAccessTokenSilently, isAuthenticated, userRefreshToggle]);

  if (isLoading) {
    return <Loading />;
  }

  if (!isLoading && !isAuthenticated) {
    return <Loading />;
  }

  return (
    <UserContext.Provider value={userContext}>
      <Outlet />
    </UserContext.Provider>
  );
};

export default AuthenticatedLayout;
